/**
 * The type of ad clip.
 *
 * @remarks
 * The primary use case for this is to determine how the UI should act during the particular clip.
 *
 * You can get this value from AdClip.type.
 * To get the current clip of an ad slot, use AdSlot.getCurrentClip().
 */
export const AdClipType = {
  /**
   * A clip which usually shows "Advertisement start" due to legal reasons.
   * No UI should be shown during this clip.
   */
  Opener: 'opener',

  /**
   * A clip which usually shows "Advertisement finished" due to legal reasons.
   * No UI should be shown during this clip.
   */
  Closer: 'closer',

  /**
   * A clip which usually shows the content provider for branding purposes.
   * No UI should be shown during this clip.
   */
  Bumper: 'bumper',

  /**
   * A real advertisement clip.
   * The UI should be shown during this clip, but seek should be disabled.
   */
  Commercial: 'commercial',

  /**
   * Non-linear ad which will be displayed around of the current content.
   * The content will be resized to fit into the designated space inside the ad.
   * The UI should be shown during this clip, but seek should be disabled.
   */
  ContentFrame: 'contentframe',

  /**
   * Non-linear ad which will be displayed on top of the current content
   * No UI should be shown during this clip.
   */
  Overlay: 'overlay',

  /**
   * Indicates the sponsored segment at the end of the AdSlot (eg. "presented by <Company>")
   */
  Sponsored: 'sponsored',

  /**
   * Fallback if the type can not be determined.
   * This should not happen and will point to an invalid ad returned by the ad server.
   *
   * If this happens the UI should deem this like a `Commercial` clip and display the UI, without seek.
   */
  Unknown: 'unknown',
} as const;

/**
 * {@inheritDoc AdClipType:var}
 */
export type AdClipType = (typeof AdClipType)[keyof typeof AdClipType];
